/**
 * metadataEngine.js
 * Orchestrates metadata loading, caching, and transformation.
 * Acts as the central metadata management service for the spreadsheet platform.
 */

import { parseColumnsToAgDefs, extractFormulaColumns } from './metadataParser';

// ─── In-memory metadata cache ─────────────────────────────────────────────────
const _cache = new Map(); // workbookId → metadata

// ─── Metadata Engine ─────────────────────────────────────────────────────────

class MetadataEngine {
  constructor() {
    this._cache         = new Map();
    this._sheetIndexMap = new Map(); // sheetId → HyperFormula sheetIndex
  }

  /**
   * Register a complete workbook metadata object.
   * Call this after fetching metadata from your Django API.
   */
  registerWorkbook(workbookId, workbookMeta) {
    // Enrich each sheet with derived properties
    const enriched = {
      ...workbookMeta,
      sheets: workbookMeta.sheets.map((sheet, idx) => this._enrichSheet(sheet, idx)),
    };

    this._cache.set(workbookId, enriched);
    return enriched;
  }

  /**
   * Get cached metadata for a workbook.
   */
  getWorkbook(workbookId) {
    return this._cache.get(workbookId) || null;
  }

  /**
   * Get metadata for a specific sheet.
   */
  getSheet(workbookId, sheetId) {
    const wb = this.getWorkbook(workbookId);
    return wb?.sheets.find((s) => s.sheetId === sheetId) || null;
  }

  /**
   * Get AG Grid columnDefs for a sheet.
   * (Derived from metadata, cached after first call.)
   */
  getAgColumnDefs(workbookId, sheetId, permissions = {}) {
    const sheet = this.getSheet(workbookId, sheetId);
    if (!sheet) return [];

    const cacheKey = `agDefs_${workbookId}_${sheetId}_${JSON.stringify(permissions)}`;
    if (!this._cache.has(cacheKey)) {
      this._cache.set(cacheKey, parseColumnsToAgDefs(sheet, permissions));
    }
    return this._cache.get(cacheKey);
  }

  /**
   * Get formula column metadata for HyperFormula seeding.
   */
  getFormulaColumns(workbookId, sheetId) {
    const sheet = this.getSheet(workbookId, sheetId);
    return sheet ? extractFormulaColumns(sheet.columns) : [];
  }

  /**
   * Register the HyperFormula sheet index for a metadata sheetId.
   */
  registerHfSheetIndex(sheetId, hfIndex) {
    this._sheetIndexMap.set(sheetId, hfIndex);
  }

  /**
   * Get the HyperFormula sheet index for a metadata sheetId.
   */
  getHfSheetIndex(sheetId) {
    return this._sheetIndexMap.get(sheetId) ?? 0;
  }

  /**
   * Get column metadata by field name.
   */
  getColumnMeta(workbookId, sheetId, field) {
    const sheet = this.getSheet(workbookId, sheetId);
    return sheet?.columns.find((c) => c.field === field) || null;
  }

  /**
   * Get ordered column fields array for a sheet.
   * Used for row → HF array conversion.
   */
  getColumnFields(workbookId, sheetId) {
    const sheet = this.getSheet(workbookId, sheetId);
    return sheet?.columns.map((c) => c.field) || [];
  }

  /**
   * Invalidate cached AG Grid defs (call when permissions change).
   */
  invalidateAgDefs(workbookId) {
    for (const key of this._cache.keys()) {
      if (key.startsWith(`agDefs_${workbookId}`)) this._cache.delete(key);
    }
  }

  /**
   * Clear all cached data.
   */
  clear() {
    this._cache.clear();
    this._sheetIndexMap.clear();
  }

  // ── Private ──────────────────────────────────────────────────────────────

  _enrichSheet(sheet, fallbackIndex) {
    return {
      ...sheet,
      // Ensure sheetId exists
      sheetId: sheet.sheetId || `sheet_${fallbackIndex}`,
      // Normalize columns
      columns: (sheet.columns || []).map((col, colIdx) => ({
        ...col,
        field:       col.field   || `col_${colIdx}`,
        headerName:  col.headerName || col.field || `Column ${colIdx + 1}`,
        datatype:    col.datatype   || 'string',
        editable:    col.editable !== false,
        width:       col.width      || 140,
      })),
      // Merge sheet-level and global permissions
      effectivePermissions: {
        canEdit:     sheet.permissions?.canEdit     ?? true,
        canSave:     sheet.permissions?.canSave     ?? true,
        canSubmit:   sheet.permissions?.canSubmit   ?? false,
        canApprove:  sheet.permissions?.canApprove  ?? false,
        canExport:   sheet.permissions?.canExport   ?? true,
        canAddRow:   sheet.permissions?.canAddRow   ?? true,
        canDeleteRow:sheet.permissions?.canDeleteRow ?? false,
      },
    };
  }
}

// Singleton
const metadataEngine = new MetadataEngine();
export default metadataEngine;
